/**
 * The session list's pure logic. Backlog item 8.
 *
 * `/auth/sessions` and `DELETE /auth/sessions/{id}` have existed since task 30 and nothing in the UI
 * asked for them. `AuthN-AuthZ-and-User-Management.md` gives the reason sessions are rows rather
 * than JWTs: *"'revoke everything now' is a control I want to actually work during an incident."*
 * A control that works and cannot be reached is half of that promise.
 *
 * Ordering and labelling live here rather than in the component so they can be tested without a
 * renderer — the same split `logStream.ts` uses for the log terminal.
 */

import type { SessionSummary } from '../api/generated';

/**
 * Sessions in the order the screen shows them: **this device first**, then most recently seen.
 *
 * 🔴 **Current-first is not cosmetic.** Every other row carries a Revoke button and this one does
 * not, so putting it anywhere else in the list makes the missing button look like a rendering fault
 * rather than a rule. It is also the row an operator checks first — *"is that me?"* — and the answer
 * to that question should not require scanning.
 *
 * `last_seen_at` descending after that, because a list of devices is read newest-activity-first and
 * the oldest row is the one most likely to be the one worth revoking.
 */
export function orderSessions(sessions: readonly SessionSummary[]): SessionSummary[] {
  return [...sessions].sort((a, b) => {
    if (a.current !== b.current) return a.current ? -1 : 1;
    return b.last_seen_at.localeCompare(a.last_seen_at);
  });
}

/** Browsers worth naming, longest-first so `Edg` is not swallowed by `Chrome`. */
const BROWSERS: ReadonlyArray<readonly [needle: string, label: string]> = [
  ['Edg/', 'Edge'],
  ['OPR/', 'Opera'],
  ['Firefox/', 'Firefox'],
  ['Chrome/', 'Chrome'],
  ['Safari/', 'Safari'],
];

/** Platforms worth naming. `iPhone` before `Mac`, because iOS Safari claims both. */
const PLATFORMS: ReadonlyArray<readonly [needle: string, label: string]> = [
  ['iPhone', 'iPhone'],
  ['iPad', 'iPad'],
  ['Android', 'Android'],
  ['Windows', 'Windows'],
  ['Macintosh', 'Mac'],
  ['Linux', 'Linux'],
];

/**
 * A short, readable device label from a user-agent string.
 *
 * ⚠ **A heuristic, and deliberately a shallow one.** Full user-agent parsing is a library and a
 * maintenance commitment, and this label's whole job is to let one operator tell their laptop from
 * their phone. The **complete** string stays available in the row's `title`, so nothing this gets
 * wrong is unrecoverable — which is what makes a shallow heuristic the right size here.
 *
 * 🔴 **Order matters in both lists and is the only subtle part.** Chrome's UA contains `Safari`,
 * Edge's contains both `Chrome` and `Safari`, and iOS Safari's contains `Macintosh`-adjacent tokens
 * — so the first match wins and the lists are ordered most-specific-first. A naive `includes`
 * ordering reports every Edge session as Safari.
 *
 * 🔴 **`null` only for a genuinely absent agent, and that distinction is a design rule rather than a
 * detail.** The register is *"`—` is not zero"* — the dash means **absence**, and an agent this
 * cannot prettify is not absent, it is a value. Returning `null` for `python-httpx/0.28.1` put two
 * identical dashes on screen where two different clients were signed in, which reads as a fault in
 * the list rather than as a limit of the labeller. Unrecognised agents come back verbatim, trimmed,
 * and the row's `title` carries the full string either way.
 */
export function deviceOf(userAgent: string | null): string | null {
  const agent = userAgent?.trim();
  if (!agent) return null;
  const browser = BROWSERS.find(([needle]) => agent.includes(needle))?.[1];
  const platform = PLATFORMS.find(([needle]) => agent.includes(needle))?.[1];
  if (browser && platform) return `${browser} on ${platform}`;
  // Truncated because a user agent can be 200 characters and this is one column of three.
  return browser ?? platform ?? (agent.length > 32 ? `${agent.slice(0, 32)}…` : agent);
}
